// Offline read-only mode for the office shell: the offline banner, telling page frames the mode
// (APP_MODE), the shell-side page messages, and the unsynced-edits warning on close.
// Moved out of app.js unchanged (docs/split-plan.md, session 7).
import { openInvoiceEditor, openInvoicesList, showStatement } from './invoices';
import { onPendingWritesChange, pendingWrites } from './trackedDb';
import { isReadOnlyMode, setReadOnlyMode, toastMessage } from './utilities';

// Offline read-only mode keeps every control usable — navigation, search,
// filters and tabs are how staff look things up offline. It used to disable
// every input/button on the page, nav rail included, which left you stuck on
// whatever page was open. Actions that can't work offline are blocked where
// they happen instead — blockIfReadOnly() guards and utilities.js's fetch
// guard, in the shell and (via broadcastAppMode) in every page iframe. Plain
// database edits go through trackedDb.js, which keeps them on the device
// until they sync.
let readOnlyUIShown = false;

function renderOfflineBanner() {
  let banner = document.getElementById('offline-readonly-banner');
  if (!banner) {
    banner = document.createElement('div');
    banner.id = 'offline-readonly-banner';
    banner.setAttribute('role', 'status');
    banner.style.cssText = 'position:fixed;left:50%;bottom:16px;transform:translateX(-50%);z-index:100000;'
      + 'max-width:calc(100vw - 32px);padding:8px 14px;border-radius:8px;background:#b3261e;color:#fff;'
      + 'font-size:13px;box-shadow:0 2px 8px rgba(0,0,0,.25);pointer-events:none;display:none';
    document.body.appendChild(banner);
  }
  const n = pendingWrites().filter(e => !e.stuck).length;
  banner.textContent = 'Offline — showing saved data. Some actions are unavailable.'
    + (n ? ` ${n} ${n === 1 ? 'change' : 'changes'} saved on this device will sync when you reconnect.` : '');
  banner.style.display = readOnlyUIShown ? 'block' : 'none';
}

onPendingWritesChange(renderOfflineBanner);
window.addEventListener('pendingdbwritessynced', (e) => {
  const n = e.detail.count;
  toastMessage(`${n} ${n === 1 ? 'change' : 'changes'} made offline earlier ${n === 1 ? 'has' : 'have'} been saved.`, 'green', 4000);
});

function applyReadOnlyUI(isRO) {
  if (isRO === readOnlyUIShown) return; // setAppMode() re-runs on every reconnect/flow
  readOnlyUIShown = isRO;
  renderOfflineBanner();
  if (!isRO) toastMessage('Back online.', 'green', 2500);
}

function broadcastAppMode(target) {
  const msg = { type: 'APP_MODE', readOnly: isReadOnlyMode(), online: navigator.onLine };
  const frames = target ? [target] : [...document.querySelectorAll('iframe.page-frame')].map(f => f.contentWindow);
  frames.forEach(w => {
    try { w?.postMessage(msg, window.location.origin); } catch {}
  });
}

window.addEventListener('message', (event) => {
  if (event.origin !== window.location.origin) return;
  // A page iframe that loaded after the last broadcast asks for the current mode.
  if (event.data?.type === 'APP_MODE_REQUEST') broadcastAppMode(event.source);
  // Pages without their own snackbar route read-only notices through here.
  else if (event.data?.type === 'APP_TOAST') toastMessage(event.data.message, event.data.color || 'red');
  // Customer page › Account: open the Invoices window or the customer's statement here in the shell.
  else if (event.data?.type === 'OPEN_INVOICES') openInvoicesList(event.data.tab || 'invoices', event.data.search || '');
  // Slips › Contracts (roadmap 6.2): a deposit or balance invoice for a slip contract, in the editor here.
  else if (event.data?.type === 'OPEN_INVOICE_DRAFT' && event.data.draft) openInvoiceEditor({ draft: event.data.draft });
  else if (event.data?.type === 'OPEN_STATEMENT' && event.data.customerId) showStatement(event.data.customerId);
  // "/" pressed inside a page iframe (utilities.js forwards it): open the rail search.
  else if (event.data?.type === 'SHELL_SEARCH') {
    const input = document.getElementById('railSearchInput');
    if (input && input.offsetParent !== null) { input.focus(); input.select(); }
  }
});

// Offline edits are kept on the device and replayed next time the app opens,
// but closing now means they wait until then — say so.
window.addEventListener('beforeunload', (event) => {
  if (!isReadOnlyMode() || !pendingWrites().length) return;
  event.preventDefault();
  event.returnValue = '';
});

export function setAppMode({ readOnly }) {
  setReadOnlyMode(!!readOnly);
  applyReadOnlyUI(!!readOnly);
  broadcastAppMode();
}
